import { CheckCircle, ArrowLeft, Bell, X } from "lucide-react";
// import colors from "../ColorConfig/colorconfig";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import thankyou from "../assets/thankyou.png";
import useTheme from "../ColorConfig/usetheme";
import SEO from "../../AASeo/Seo";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

export default function ThankYouPage({ onBackToForm }) {
  const colors = useTheme();
  const navigate = useNavigate();
  const navigationExecuted = useRef(false);
  const [showPopup, setShowPopup] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    // Ensure body is scrollable when component mounts
    document.body.style.overflow = "auto";
    document.body.style.position = "static";
    document.body.style.height = "auto";

    window.scrollTo(0, 0);

    // Preload the audio
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.volume = 0.7;
    }

    const showTimer = setTimeout(() => {
      setShowPopup(true);
      playAudio();
    }, 1000);

    const hideTimer = setTimeout(() => {
      setShowPopup(false);
    }, 30000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      // Clean up any styles when component unmounts
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
      document.body.style.height = "auto";
    };
  }, []);

  const playAudio = () => {
    if (audioPlayed || !audioRef.current) return;

    try {
      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("Audio played successfully");
            setAudioPlayed(true);
          })
          .catch((error) => {
            console.error("Audio playback failed:", error);
            // If autoplay fails, we'll try to play on next user interaction
            setupUserInteractionAudio();
          });
      }
    } catch (error) {
      console.error("Audio playback error:", error);
      setupUserInteractionAudio();
    }
  };

  const setupUserInteractionAudio = () => {
    const handleUserInteraction = () => {
      if (!audioPlayed && audioRef.current) {
        audioRef.current
          .play()
          .then(() => {
            console.log("Audio played after user interaction");
            setAudioPlayed(true);
          })
          .catch((e) => console.error("Fallback audio play failed:", e));
      }
      document.removeEventListener("click", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };

    document.addEventListener("click", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);
  };

  const handleBackClick = () => {
    if (navigationExecuted.current) return;
    navigationExecuted.current = true;

    window.scrollTo({ top: 0, behavior: "smooth" });

    if (onBackToForm) {
      onBackToForm();
    } else {
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 100);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <>
      <SEO
        title="Thank You | IDLBAY - Message Sent"
        description="Thank you for contacting IDLBAY. Your message has been sent successfully. We will get back to you soon."
        keywords="thank you, message sent, contact confirmation, IDLBAY"
        url="https://idlbay.com/thankyou"
        organization={organization}
      />
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://i.pinimg.com/736x/fa/e2/7c/fae27c577dd35c69d24422fda47d778d.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "10px",
          position: "relative",
          overflowX: "hidden",
        }}
      >
        {/* Audio element for success sound */}
        <audio
          ref={audioRef}
          preload="auto"
          onLoadedData={() => console.log("Audio loaded successfully")}
          onError={(e) => console.error("Audio loading error:", e)}
        >
          <source src={require("../assets/sounds/tq3.mp3")} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>

        {/* Notification Popup */}
        {showPopup && (
          <div
            style={{
              position: "fixed",
              top: "70px",
              right: "20px",
              zIndex: 1000,
              animation:
                "popupSlideIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
              marginLeft: "20px",
            }}
          >
            <div
              style={{
                background: `linear-gradient(135deg, var(--color-primary)20 0%, ${colors.bodyBlack}95 50%, var(--color-primary)10 100%)`,
                backdropFilter: "blur(20px)",
                border: `1px solid var(--color-primary)30`,
                borderRadius: "16px",
                padding: "20px",
                minWidth: "320px",
                maxWidth: "400px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "0",
                  right: "0",
                  bottom: "0",
                  animation: "gradientShift 3s ease-in-out infinite",
                  zIndex: -1,
                }}
              ></div>

              <button
                onClick={closePopup}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  background: "transparent",
                  border: "none",
                  color: colors.gray,
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = `var(--color-primary)` + "20";
                  e.target.style.color = colors.white;
                  e.target.style.transform = "scale(1.1)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.color = colors.gray;
                  e.target.style.transform = "scale(1)";
                }}
              >
                <X size={18} />
              </button>

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    background: `linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%)`,
                    borderRadius: "12px",
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "iconPulse 2s ease-in-out infinite",
                  }}
                >
                  <Bell size={20} color={colors.white} />
                </div>

                <div style={{ flex: 1, paddingRight: "20px" }}>
                  <h3
                    style={{
                      color: colors.white,
                      fontSize: "16px",
                      fontWeight: "600",
                      margin: "0 0 8px 0",
                      animation: "textSlideIn 0.8s ease-out 0.3s both",
                    }}
                  >
                    Message Sent Successfully!
                  </h3>

                  <p
                    style={{
                      color: colors.gray,
                      fontSize: "14px",
                      margin: "0 0 12px 0",
                      lineHeight: "1.5",
                      animation: "textSlideIn 0.8s ease-out 0.5s both",
                    }}
                  >
                    Thank you for contacting us. We'll get back to you within 24
                    hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-20px) rotate(5deg); }
            66% { transform: translateY(-10px) rotate(-3deg); }
          }
          @keyframes slideUp {
            from { transform: translateY(50px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes fadeInScale {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }

          @keyframes popupSlideIn {
            0% { 
              transform: translateX(400px) scale(0.8); 
              opacity: 0; 
            }
            60% { 
              transform: translateX(-10px) scale(1.05); 
              opacity: 0.8; 
            }
            80% { 
              transform: translateX(5px) scale(0.98); 
              opacity: 0.9; 
            }
            100% { 
              transform: translateX(0) scale(1); 
              opacity: 1; 
            }
          }

          @keyframes iconPulse {
            0%, 100% { 
              transform: scale(1) rotate(0deg); 
              box-shadow: 0 4px 12px var(--color-primary)40; 
            }
            50% { 
              transform: scale(1.1) rotate(5deg); 
              box-shadow: 0 6px 20px var(--color-primary)60; 
            }
          }

          @keyframes textSlideIn {
            from { 
              transform: translateY(10px); 
              opacity: 0; 
            }
            to { 
              transform: translateY(0); 
              opacity: 1; 
            }
          }

          @keyframes progressBar {
            from { 
              width: 100%; 
              opacity: 1;
            }
            90% { 
              width: 5%; 
              opacity: 1;
            }
            100% { 
              width: 0%; 
              opacity: 0;
            }
          }

          @keyframes gradientShift {
            0%, 100% { 
              background-position: 0% 50%; 
            }
            50% { 
              background-position: 100% 50%; 
            }
          }

          @keyframes imageZoom {
            from { 
              transform: scale(0.8); 
              opacity: 0; 
            }
            to { 
              transform: scale(1); 
              opacity: 1; 
            }
          }

          @keyframes textOverlaySlide {
            from { 
              transform: translateY(30px); 
              opacity: 0; 
            }
            to { 
              transform: translateY(0); 
              opacity: 1; 
            }
          }

          @media (max-width: 768px) {
            .popup-container {
              top: 10px !important;
              right: 10px !important;
              left: 10px !important;
              min-width: auto !important;
              max-width: none !important;
            }
            
            .hero-image {
              width: 280px !important;
              height: 200px !important;
            }
            
            .overlay-text {
              font-size: 2rem !important;
            }
          }
        `}</style>

        <div
          style={{
            borderRadius: "25px",
            textAlign: "center",
            maxWidth: "850px",
            width: "100%",
            position: "relative",
            zIndex: 2,
            animation: "slideUp 0.8s ease-out",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "450px",
              height: "350px",
              margin: "0 auto 1px",
              borderRadius: "20px",
              overflow: "hidden",
              marginTop: "-30px",
              animation: "imageZoom 1s ease-out",
            }}
            className="hero-image"
          >
            <img
              src={thankyou}
              alt="Thank You"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "20px",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.style.background = `linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%)`;
              }}
            />

            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "20px",
              }}
            ></div>

            <div
              style={{
                position: "absolute",
                top: "37%",
                left: "10%",
                transform: "translate(-50%, -50%)",
                color: colors.white,
                textAlign: "center",
                animation: "textOverlaySlide 1.2s ease-out 0.5s both",
              }}
            >
              <h1
                style={{
                  fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
                  fontWeight: "800",
                  margin: 0,
                  textShadow: "2px 2px 8px rgba(0,0,0,0.5)",
                  filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.3))",
                }}
                className="overlay-text"
              >
                Thank You!
              </h1>
            </div>
          </div>

          <p
            style={{
              color: colors.white,
              fontSize: "clamp(1.1rem, 3vw, 1.3rem)",
              fontWeight: "500",
              animation: "fadeInScale 0.8s ease-out 0.8s both",
            }}
          >
            Your message has been sent successfully!
          </p>

          <p
            style={{
              color: colors.gray,
              fontSize: "clamp(0.95rem, 2.5vw, 1.05rem)",
              lineHeight: "1.7",
              maxWidth: "450px",
              margin: "0 auto 50px",
              animation: "fadeInScale 0.8s ease-out 1s both",
            }}
          >
            We appreciate you reaching out to us. Our team will review your
            message and get back to you within 24 hours.
          </p>

          <div
            style={{
              display: "flex",
              gap: "5px",
              justifyContent: "center",
              flexWrap: "wrap",
              animation: "fadeInScale 0.8s ease-out 1.2s both",
            }}
          >
            <button
              onClick={handleBackClick}
              disabled={navigationExecuted.current}
              style={{
                background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.white} 100%)`,
                color: colors.black,
                border: "none",
                padding: "10px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "400",
                cursor: navigationExecuted.current ? "default" : "pointer",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                minWidth: "160px",
                justifyContent: "center",
                opacity: navigationExecuted.current ? 0.7 : 1,
                marginTop: "-40px",
              }}
              onMouseOver={(e) => {
                if (!navigationExecuted.current) {
                  e.target.style.transform = "translateY(-3px) scale(1.02)";
                  e.target.style.boxShadow = `0 12px 30pxvar(--color-primary)40`;
                }
              }}
              onMouseOut={(e) => {
                if (!navigationExecuted.current) {
                  e.target.style.transform = "translateY(0) scale(1)";
                  e.target.style.boxShadow = `0 8px 20px var(--color-primary)30`;
                }
              }}
            >
              <ArrowLeft size={18} />
              {navigationExecuted.current ? "Going back..." : "Back to Home"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
